import { ArticleSummary, PaginatedResult } from '../../shared/types';
import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';

export const GET_ARTICLES = gql`
    query getArticles($params: GetArticleInput, $first: Int, $page: Int) {
        getArticles(params: $params, first: $first, page: $page) {
            totalCount
            pageInfo {
                endCursor
                hasNextPage
            }
            documents {
                _id
                publishedBy {
                    _id
                    name
                    type
                }
                title
                summary
                categories {
                    _id
                    name
                }
                premiumChannelDetails {
                    _id
                    name
                }
                rootCategories {
                    _id
                    name
                }
                publishDate
                isPremium
                isPremiumActive
                isLocked
                canSeeFullVersion
                userGuideScopes
            }
        }
    }
`;

interface UseGetArticleListOptions {
    batchSize: number;
    page?: number;
    onlyMySubscriptions?: boolean;
    excludeMySubscriptions?: boolean;
    freeOnly?: boolean;
    isUserGuideOnly?: boolean;
    userGuideScopes?: string[];
}

export const useGetArticleList = ({
    batchSize,
    freeOnly,
    excludeMySubscriptions,
    onlyMySubscriptions,
    isUserGuideOnly,
    userGuideScopes,
    page = 0,
}: UseGetArticleListOptions) => {
    const query = useQuery<{ getArticles: PaginatedResult<ArticleSummary> }>(
        GET_ARTICLES,
        {
            fetchPolicy: 'network-only',
            variables: {
                first: batchSize,
                params: {
                    onlyMySubscriptions,
                    excludeMySubscriptions,
                    freeOnly,
                    isUserGuideOnly,
                    userGuideScopes,
                },
            },
        }
    );

    const data = query.data?.getArticles;

    const fetchMore = () => {
        query.fetchMore({
            variables: { page },
            updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult) return prev;

                return {
                    getArticles: {
                        ...prev.getArticles,

                        totalCount: prev.getArticles.totalCount,
                        documents: [
                            ...prev.getArticles.documents,
                            ...fetchMoreResult.getArticles.documents,
                        ],
                        pageInfo: {
                            ...fetchMoreResult.getArticles.pageInfo,
                            endCursor:
                                fetchMoreResult.getArticles.pageInfo
                                    .endCursor ??
                                prev.getArticles.pageInfo.endCursor,
                        },
                    },
                };
            },
        });
    };

    return {
        loading: query.loading,
        articles: data?.documents,
        totalCount: data?.totalCount,
        hasNextPage: data?.pageInfo.hasNextPage,
        fetchMore,
    };
};
